import React, { useEffect, useRef, useState } from "react";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import CloseFile from "static/images/CloseFile";
import Button from "components/common/atoms/Button";
import type {
  WhatsAppSideBarProps,
  ChatMessage,
  ListenerDataProps,
  ChatMessagePayload,
  MediaPayload,
} from "../annotations/WhatsAppAnnotations";
import ChatMessageList from "../components/ChatMessageList";
import ChatInput from "../components/ChatInput";
import { SendMessage } from "../services/SendMessageService";
import { FetchMessages } from "../services/FetchMessagesService";
import "../css/whatsApp.css";
import ImageField from "components/common/atoms/ImageField";
import Icon from "../../../../static/images/UserIcon.svg";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import Call from "static/images/Call";
import CustomNotify from "components/common/atoms/CustomNotify";
import { Link } from "react-router-dom";
import { FILE_URL, PHONE_CALL_URL } from "../../../../routes/ApiEndpoints";
import { useWhatsAppListener } from "../webSocket/useWhatsAppListener";
import secureLocalStorage from "react-secure-storage";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import { UpdateMessage } from "../services/UpdateMessageService";
import PreviewModal from "../components/PreviewModal";
import { uploadMediaFiles } from "../components/uploadMediaFiles";
import LoadingOverlay from "components/common/utlis/LoadingOverlay";
import TextEllipsis from "components/common/atoms/TextEllipsis";
import { validateFiles } from "../services/MediaValidator";

const WhatsAppSideBar: React.FC<WhatsAppSideBarProps> = ({
  onClose,
  className,
  contentScroll,
  whatsAppData,
  getWidth,
}) => {
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [page, setPage] = useState(2);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const initialMessageState: ChatMessagePayload = {
    type: "",
    media: [],
    message: "",
  };
  const [newMessage, setNewMessage] =
    useState<ChatMessagePayload>(initialMessageState);
  const containerRef = useRef<HTMLDivElement>(null);
  const topDivRef = useRef<HTMLDivElement>(null);
  const bottomDivRef = useRef<HTMLDivElement>(null);
  const isInitialLoadRef = useRef(true);
  const scrollLockRef = useRef(false); // prevents multiple calls per scroll top event
  const pageLimit = 15;

  const loadMessages = async () => {
    if (!isInitialLoadRef.current && (isLoading || !hasMore)) {return;}
    setIsLoading(true);

    const data = {
      ...whatsAppData,
      nOR: pageLimit,
      page: isInitialLoadRef.current === true ? 1 : page,
    };

    const response = await FetchMessages(data);

    if (response.status === 200 && Array.isArray(response.data.messages)) {
      const newMessages = response.data.messages;

      if (newMessages.length < pageLimit) {
        setHasMore(false);
      }

      if (newMessages.length > 0) {
        setChatMessages((prev) => {
          const allMessages = [...newMessages, ...prev];

          const uniqueMessagesMap = new Map();
          allMessages.forEach((msg) => {
            uniqueMessagesMap.set(msg.id, msg); // will keep the latest by id
          });

          return Array.from(uniqueMessagesMap.values()).sort(
            (a, b) =>
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
        });
      }

      if (page === 1) {
        isInitialLoadRef.current = true;
      }
    }

    setIsLoading(false);
  };
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Initial load
  useEffect(() => {
    setChatMessages([]);
    setPage(2);
    setHasMore(true);
    setIsLoading(false);
    isInitialLoadRef.current = true;

    if (sidebarRef.current && getWidth) {
      const sidebarWidth = sidebarRef.current.offsetWidth;
      getWidth(sidebarWidth);
    }
    // Load initial messages
    if (whatsAppData.to !== "") {
      loadMessages();
      setShowPreview(false);
      setNewMessage(initialMessageState);
    }
  }, [containerRef.current, whatsAppData?.to]);

  // Scroll to bottom on first load
  useEffect(() => {
    if (
      isInitialLoadRef.current &&
      chatMessages.length > 0 &&
      bottomDivRef.current
    ) {
      requestAnimationFrame(() => {
        bottomDivRef.current?.scrollIntoView({ behavior: "smooth" });
      });
      // Wait for scroll to settle before allowing scroll events
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 500);
    }
  }, [chatMessages]);

  // Scroll handler
  const handleScroll = () => {
    const scrollContainer = containerRef.current;
    if (!scrollContainer) {return;}
    const currentScrollTop = scrollContainer.scrollTop;
    if (
      !isInitialLoadRef.current &&
      currentScrollTop <= 300 &&
      currentScrollTop === 0 &&
      hasMore &&
      !isLoading &&
      !scrollLockRef.current
    ) {
      setPage((prev) => prev + 1);
      scrollLockRef.current = true;
      const previousScrollHeight = scrollContainer.scrollHeight;
      // Add a small offset (e.g., 20px) to move slightly down from the top
      const safeOffset = 20;

      loadMessages().finally(() => {
        // Smooth scroll to new position:
        const newScrollHeight = scrollContainer.scrollHeight;
        const scrollDifference = newScrollHeight - previousScrollHeight;
        scrollContainer.scrollTo({
          top: currentScrollTop + scrollDifference + safeOffset,
          behavior: "smooth", // <-- smooth scroll here
        });
        // Optionally delay unlocking to avoid rapid re-triggers
        setTimeout(() => {
          scrollLockRef.current = false;
        }, 300);
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldType: string,
    index?: number | null
  ) => {
    const value = e.target.value;

    if (fieldType === "text") {
      setNewMessage((prev) => ({
        ...prev,
        message: value,
        type: "text",
      }));
    } else if (fieldType === "media" && typeof index === "number") {
      setNewMessage((prev) => {
        const updatedMedia = [...prev.media];
        updatedMedia[index] = {
          ...updatedMedia[index],
          mediaCaption: value,
        };

        return {
          ...prev,
          media: updatedMedia,
          type: "media",
        };
      });
    }
  };

  const handleSendMessage = async () => {
    if (newMessage.type === "text" && !newMessage.message.trim()) {return;}
    setIsMessageSending(true);
    try {
      let finalMessage = { ...newMessage };

      // Upload media if needed
      if (newMessage.type === "media" && newMessage.media.length > 0) {
        // Determine if any media file is an image
        const isImage = newMessage.media.some((media) =>
          media.mediaType?.startsWith("image")
        );

        const s3Path = isImage ? "whatsapp-images" : "whatsapp-documents";

        const uploadedMedia = await uploadMediaFiles(
          newMessage.media,
          whatsAppData.personId,
          s3Path
        );
        if (uploadedMedia.length > 0) {setShowPreview(false);}
        finalMessage = {
          type: "media",
          media: uploadedMedia,
          message: "",
        };

        setNewMessage(finalMessage); // update state with uploaded media info
      }

      // Send message with uploaded media or text
      const response = await SendMessage(whatsAppData, finalMessage);
      if (response?.status === 200) {
        secureLocalStorage.setItem("whatsappListener", true);
        setChatMessages((prev) => [...prev, ...response.data]);
        setNewMessage(initialMessageState);
        setIsMessageSending(false);
        setShowPreview(false);
        // Scroll to bottom after sending
        setTimeout(() => {
          bottomDivRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        CustomNotify({ type: "error", message: response.msg });
      }
    } catch (error) {
      CustomNotify({
        type: "error",
        message: "Something went wrong sending the message.",
      });
      console.error("Send message error:", error);
    }
  };

  const handleListenerMessage = async (newData: ListenerDataProps) => {
    secureLocalStorage.setItem("whatsappListener", true);
    if (whatsAppData.to === newData?.from) {
      setChatMessages((prev) => [...prev, newData.message]);
      const response = await UpdateMessage(newData);
      if (response.status !== 200) {
        console.log(response);
      }
    }
  };

  useWhatsAppListener({
    "message.received": handleListenerMessage,
  });

  //file upload section
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles) {return;}
    const validFiles = validateFiles(selectedFiles, newMessage.media);
    if (validFiles.length === 0) {return;}
    const newFilesArray: MediaPayload[] = [];
    let filesLoaded = 0;
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newFilesArray.push({
          mediaCaption: "",
          mediaType: file.type,
          mediaFile: file,
          mediaName: file.name,
          mediaPath: reader.result as string,
          mediaId: null,
          mediaSize: file.size,
        });

        filesLoaded++;
        if (filesLoaded === validFiles.length) {
          setNewMessage((prev) => ({
            ...prev,
            type: "media",
            media: [...prev.media, ...newFilesArray],
          }));
          setShowPreview(true);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handlePreviewClose = () => {
    setShowPreview(false);
    setNewMessage(initialMessageState);
  };

  const handleRemoveFile = (index: number) => {
    setNewMessage((prev) => {
      const updatedMedia = [...prev.media];
      updatedMedia.splice(index, 1);

      return {
        ...prev,
        media: updatedMedia,
        type: updatedMedia.length > 0 ? "media" : "",
      };
    });

    setShowPreview((prev) => {
      return newMessage.media.length <= 1 ? false : prev;
    });
  };

  return (
    <AccessControl
      requiredPermissions={[{ permission: "Whatsapp", read: true }]}
      renderNoAccess={true}
    >
      <div ref={sidebarRef} className={`scrollBarDesign ${className}`}>
        {whatsAppData.to !== "" && (
          <>
            {/* Header */}
            <div className="chat-header d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center justify-content-start">
                <div className="chat-user-wrapper">
                  <div className="chat-users-icon">
                    <ImageField
                      src={
                        whatsAppData?.profilePicPath !== null &&
                        whatsAppData?.profilePicPath !== ""
                          ? `${FILE_URL}/${whatsAppData.profilePicPath}`
                          : Icon
                      }
                      className="whatsapp-profile"
                    />
                  </div>
                </div>
                <div className="chat-selected-name fw-bold color-dark-pink">
                  <TextEllipsis
                    title={whatsAppData?.name}
                    label={whatsAppData?.name}
                    width="8vw"
                  />
                  <div className="whatsappNo">+{whatsAppData.to}</div>
                </div>
              </div>
              <div className="d-flex">
                <span
                  className="table-action-icons cursor-pointer"
                  style={{ paddingRight: "1vw" }}
                  title={t("Call")}
                >
                  <Link
                    to={`${PHONE_CALL_URL}${"%2B" + whatsAppData.to}`}
                    target="_blank"
                  >
                    <Call />
                  </Link>
                </span>
                {onClose && (
                  <Button
                    handleClick={onClose}
                    className="table-action-icons border-0 bg-transparent"
                  >
                    <span title={t("Close")}>
                      <CloseFile />
                    </span>
                  </Button>
                )}
              </div>
            </div>

            {/* Chat Body with scroll listener */}
            <div
              ref={containerRef} // Proper scroll container
              className={`chat-body scrollBarDesign ${contentScroll}`}
              onScroll={handleScroll}
            >
              {isLoading && <LoadingIcon />}
              <div ref={topDivRef} />
              <ChatMessageList messages={chatMessages} loading={isLoading} />
              <div ref={bottomDivRef} />
            </div>

            {/* Chat Input */}
            <ChatInput
              value={newMessage}
              onChange={handleChange}
              onSend={handleSendMessage}
              handleFileUpload={handleFileUpload}
              isMessageSending={isMessageSending}
            />
          </>
        )}
      </div>
      {isMessageSending && (
        <div className="whatsapp-overlay">
          <LoadingOverlay display="flex" />
        </div>
      )}
      {showPreview && (
        <PreviewModal
          files={newMessage}
          handlePreviewClose={handlePreviewClose}
          handleRemoveFile={handleRemoveFile}
          handleSendMessage={handleSendMessage}
          handleChange={handleChange}
          handleFileUpload={handleFileUpload}
          isMessageSending={isMessageSending}
        />
      )}
    </AccessControl>
  );
};

export default translate(WhatsAppSideBar);
